/**
 * HealthLoop AI Personality Module
 * Defines consistent voice, tone, and response patterns across all interactions
 * 
 * Personality Identity:
 * A calm, supportive, intelligent health companion that helps users understand
 * their body early and take simple actions.
 */

// ============================================================================
// PERSONALITY CORE
// ============================================================================

export const PERSONALITY_TRAITS = {
  voice: 'calm, supportive, intelligent health companion',
  values: ['empathy', 'clarity', 'empowerment', 'trust'],
  avoids: ['judgment', 'medical jargon', 'diagnosis', 'alarm']
};

export const TONE_GUIDELINES = {
  friendly: 'Human and warm, not robotic',
  supportive: 'Encouraging, not judgmental',
  clear: 'Simple language, no complex terms',
  humble: 'Acknowledges limitations, defers to professionals'
};

// ============================================================================
// RESPONSE STRUCTURE
// ============================================================================

/**
 * Every AI response follows: Acknowledge → Insight → Actions → Encourage
 */
export const RESPONSE_STRUCTURE = {
  acknowledge: 'Show empathy and understanding',
  insight: 'Provide simple, non-diagnostic observation',
  actions: 'Suggest 1-3 practical steps',
  encourage: 'Offer supportive closing'
};

// ============================================================================
// LANGUAGE TRANSFORMATIONS
// ============================================================================

/**
 * Transform medical/technical language to friendly, supportive language
 */
export const LANGUAGE_TRANSFORMATIONS = {
  // Medical → Supportive
  diagnosis: {
    avoid: ['You have', 'You are diagnosed with', 'This indicates', 'You suffer from'],
    use: ['It looks like', 'Your body may be', 'This could be related to', 'You might be experiencing']
  },
  
  // Negative → Neutral/Positive
  negative: {
    avoid: ['Bad', 'Wrong', 'Failed', 'Poor', 'Unhealthy'],
    use: ['Could be better', 'Room for improvement', 'Let\'s work on', 'Consider adjusting']
  },
  
  // Alarming → Calm
  alarm: {
    avoid: ['Emergency!', 'Danger!', 'Serious problem!', 'Critical!'],
    use: ['This may need attention', 'Consider checking in with', 'It\'s worth discussing', 'You might want to']
  },
  
  // Commands → Suggestions
  commands: {
    avoid: ['You must', 'You should', 'You need to', 'It is required'],
    use: ['You might try', 'Consider', 'It could help to', 'One option is']
  }
};

// ============================================================================
// DYNAMIC TONE MODULATION
// ============================================================================

/**
 * Adjust tone based on user state and context
 */
export const DYNAMIC_TONES = {
  // User doing well
  positive: {
    acknowledge: [
      'Great work!',
      'You\'re doing well',
      'Nice progress',
      'That\'s wonderful'
    ],
    encourage: [
      'Keep it up!',
      'You\'ve got this',
      'Proud of you',
      'You\'re on the right track'
    ]
  },
  
  // User struggling
  encouraging: {
    acknowledge: [
      'We see you\'re working on this',
      'Every step counts',
      'It\'s okay to have tough days',
      'You\'re not alone in this'
    ],
    encourage: [
      'Tomorrow is a fresh start',
      'Small steps lead to big changes',
      'You can do this',
      'Be kind to yourself'
    ]
  },
  
  // Serious symptoms (calm urgency)
  concerned: {
    acknowledge: [
      'Thank you for sharing this',
      'I understand this is concerning',
      'Let\'s make sure you get the right support'
    ],
    encourage: [
      'Getting help early is the right step',
      'You know your body best',
      'It\'s always okay to ask for help',
      'Trust your instincts'
    ]
  },
  
  // Neutral/informative
  neutral: {
    acknowledge: [
      'Here\'s what I\'m noticing',
      'Let\'s look at this together',
      'Based on what you shared',
      'I\'ve analyzed your information'
    ],
    encourage: [
      'Let me know how it goes',
      'I\'m here to help',
      'Check back anytime',
      'You\'re taking good care of yourself'
    ]
  }
};

// ============================================================================
// EMPATHY PHRASES
// ============================================================================

/**
 * Human-like acknowledgment phrases
 */
export const EMPATHY_PHRASES = {
  understanding: [
    'I hear you',
    'That makes sense',
    'I understand',
    'Thanks for sharing',
    'I see what you mean'
  ],
  validation: [
    'It\'s completely normal to feel this way',
    'Many people experience this',
    'You\'re not alone',
    'This is a common experience',
    'Your feelings are valid'
  ],
  encouragement: [
    'You\'re taking the right steps',
    'You\'re being proactive about your health',
    'This is great self-awareness',
    'You know your body well'
  ]
};

// ============================================================================
// RESPONSE GENERATORS
// ============================================================================

/**
 * Generate a complete AI response following personality guidelines
 */
export const generatePersonalizedResponse = (config) => {
  const {
    userState = 'neutral', // positive, encouraging, concerned, neutral
    insight = '',
    actions = [],
    severity = 'low',
    context = ''
  } = config;

  const toneSet = DYNAMIC_TONES[userState] || DYNAMIC_TONES.neutral;
  
  // 1. Acknowledge (with empathy)
  const acknowledge = getRandomItem(toneSet.acknowledge);
  
  // 2. Insight (non-diagnostic)
  const formattedInsight = formatInsight(insight);
  
  // 3. Actions (practical, 1-3 items)
  const formattedActions = formatActions(actions);
  
  // 4. Encourage (supportive close)
  const encourage = getRandomItem(toneSet.encourage);

  return {
    acknowledge,
    insight: formattedInsight,
    actions: formattedActions,
    encourage,
    full: `${acknowledge}. ${formattedInsight}${formattedActions ? ' ' + formattedActions : ''}. ${encourage}.`
  };
};

/**
 * Format insight with non-diagnostic language
 */
const formatInsight = (insight) => {
  if (!insight) return '';
  
  // Replace diagnostic language
  let formatted = insight;
  
  // Transform "You are" to "Your body may be"
  formatted = formatted.replace(/You are (\w+)/gi, 'Your body may be $1');
  
  // Transform "You have" to "It looks like you might have"
  formatted = formatted.replace(/You have/gi, 'It looks like your body is showing signs of');
  
  // Soften with hedging language
  if (!formatted.includes('may') && !formatted.includes('might') && !formatted.includes('possibly')) {
    formatted = formatted.replace(/^/, 'It looks like ');
  }
  
  return formatted;
};

/**
 * Format actions into friendly suggestions
 */
const formatActions = (actions) => {
  if (!actions || actions.length === 0) return '';
  
  if (actions.length === 1) {
    return `You might try: ${actions[0].toLowerCase()}.`;
  }
  
  return `Here are a few things that could help: ${actions.slice(0, 3).join(', ')}.`;
};

/**
 * Get random item from array (adds variety)
 */
const getRandomItem = (arr) => {
  return arr[Math.floor(Math.random() * arr.length)];
};

// ============================================================================
// SPECIFIC RESPONSE PATTERNS
// ============================================================================

/**
 * Health status card messages
 */
export const healthStatusMessages = {
  sleep: {
    good: 'Your sleep looks consistent. That\'s great for your overall wellbeing.',
    poor: 'Your body may need more rest. Consider going to bed a bit earlier tonight.',
    neutral: 'Sleep is so important. Let\'s track this together.'
  },
  hydration: {
    good: 'Nice work staying hydrated! Your body is thanking you.',
    poor: 'It looks like your body may need more water today. Try keeping a bottle nearby.',
    neutral: 'Hydration powers everything. Let\'s keep an eye on this.'
  },
  activity: {
    good: 'You\'re moving well! Regular activity makes a real difference.',
    poor: 'Your body might feel better with a bit of movement. Even a short walk helps.',
    neutral: 'Movement is medicine. Every step counts.'
  },
  stress: {
    good: 'You\'re managing stress well. Keep using what works for you.',
    poor: 'Stress can affect how you feel. Consider taking a few deep breaths.',
    neutral: 'Stress is normal. Let\'s work on managing it together.'
  },
  mood: {
    good: 'It\'s wonderful to see you feeling positive.',
    poor: 'Tough days happen. Be kind to yourself and reach out if you need support.',
    neutral: 'How you feel matters. I\'m here to help track patterns.'
  }
};

/**
 * Progress insight messages
 */
export const progressInsightMessages = {
  improving: 'I\'m noticing some positive changes. You\'re moving in the right direction.',
  stable: 'You\'re maintaining consistency. That\'s really valuable.',
  declining: 'I see some areas where things could improve. Let\'s work on this together.',
  new_pattern: 'Here\'s an interesting pattern I noticed. This might be helpful to know.'
};

/**
 * Symptom checker messages
 */
export const symptomCheckerMessages = {
  mild: {
    open: 'Thank you for sharing how you\'re feeling.',
    close: 'Most lifestyle-related symptoms improve with rest and self-care. You know your body best.'
  },
  moderate: {
    open: 'I understand this has been bothering you.',
    close: 'If this continues, it\'s worth checking in with a healthcare provider. You\'re being smart about this.'
  },
  urgent: {
    open: 'Thank you for being open about your symptoms.',
    close: 'Getting help early is always the right choice. You\'re not alone in this.'
  }
};

/**
 * Encouragement for habit tracking
 */
export const habitTrackingMessages = {
  streak: {
    1: 'Great start! You showed up today.',
    3: 'Three days in a row! You\'re building momentum.',
    7: 'One week! You\'re creating a real habit.',
    14: 'Two weeks! This is becoming part of your routine.',
    30: 'One month! You\'re amazing. This is life-changing.'
  },
  missed: {
    gentle: 'No worries about yesterday. Today is a fresh start.',
    understanding: 'Life happens. What matters is coming back.',
    motivating: 'You\'ve done this before. You can do it again.'
  }
};

// ============================================================================
// RESPONSE EXAMPLES
// ============================================================================

export const RESPONSE_EXAMPLES = {
  // Example 1: Good progress
  goodProgress: generatePersonalizedResponse({
    userState: 'positive',
    insight: 'You\'ve been consistent with hydration this week',
    actions: ['Keep your water bottle nearby', 'Try adding lemon for variety'],
    severity: 'low'
  }),
  
  // Example 2: Needs improvement
  needsImprovement: generatePersonalizedResponse({
    userState: 'encouraging',
    insight: 'Your sleep has been inconsistent lately',
    actions: ['Try going to bed 30 minutes earlier', 'Create a calming bedtime routine'],
    severity: 'low'
  }),
  
  // Example 3: Serious symptom
  seriousSymptom: generatePersonalizedResponse({
    userState: 'concerned',
    insight: 'You\'ve reported dizziness along with not drinking much water',
    actions: ['Sit down and drink water slowly', 'Contact NHS 111 for guidance'],
    severity: 'high'
  })
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Determine user state from context
 */
export const determineUserState = (context) => {
  const { streaks = 0, completed = 0, symptoms = [], lifestyle = {} } = context;
  
  // Serious symptoms = concerned
  const hasRedFlags = symptoms.some(s => ['dizziness', 'chest_pain', 'severe'].some(flag => s.includes(flag)));
  if (hasRedFlags) return 'concerned';
  
  // Good streaks and completion = positive
  if (streaks >= 3 && completed >= 75) return 'positive';
  
  // Low completion or poor lifestyle = encouraging
  if (completed < 50 || Object.values(lifestyle).filter(Boolean).length < 2) return 'encouraging';
  
  // Default = neutral
  return 'neutral';
};

/**
 * Apply personality to any message
 */
export const applyPersonality = (message, tone = 'neutral') => {
  // Remove any commands
  let friendly = message.replace(/You must|You should|You need to/gi, 'You might try');
  
  // Remove diagnosis language
  friendly = friendly.replace(/You have|You are diagnosed/gi, 'It looks like your body is showing signs of');
  
  // Soften negative words
  friendly = friendly.replace(/bad|poor|unhealthy/gi, (match) => {
    const replacements = { bad: 'could be better', poor: 'needs attention', unhealthy: 'not optimal' };
    return replacements[match.toLowerCase()] || match;
  });
  
  return friendly;
};

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

export default {
  PERSONALITY_TRAITS,
  TONE_GUIDELINES,
  RESPONSE_STRUCTURE,
  LANGUAGE_TRANSFORMATIONS,
  DYNAMIC_TONES,
  EMPATHY_PHRASES,
  generatePersonalizedResponse,
  healthStatusMessages,
  progressInsightMessages,
  symptomCheckerMessages,
  habitTrackingMessages,
  determineUserState,
  applyPersonality,
  RESPONSE_EXAMPLES
};
